"use client";

import { c as _c } from "react/compiler-runtime";
import { type MotionProps, motion, useInView } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
interface TypingAnimationProps extends MotionProps {
  children?: string;
  words?: string[];
  className?: string;
  duration?: number;
  typeSpeed?: number;
  deleteSpeed?: number;
  delay?: number;
  pauseDelay?: number;
  loop?: boolean;
  as?: React.ElementType;
  startOnView?: boolean;
  showCursor?: boolean;
  blinkCursor?: boolean;
  cursorStyle?: "line" | "block" | "underscore";
}
export function TypingAnimation(t0) {
  const $ = _c(53);
  let children;
  let className;
  let deleteSpeed;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let typeSpeed;
  let words;
  if ($[0] !== t0) {
    ({
      children,
      words,
      className,
      duration: t1,
      typeSpeed,
      deleteSpeed,
      delay: t2,
      pauseDelay: t3,
      loop: t4,
      as: t5,
      startOnView: t6,
      showCursor: t7,
      blinkCursor: t8,
      cursorStyle: t9,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = deleteSpeed;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
    $[14] = typeSpeed;
    $[15] = words;
  } else {
    children = $[1];
    className = $[2];
    deleteSpeed = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
    typeSpeed = $[14];
    words = $[15];
  }
  const duration = t1 === undefined ? 100 : t1;
  const delay = t2 === undefined ? 0 : t2;
  const pauseDelay = t3 === undefined ? 1000 : t3;
  const loop = t4 === undefined ? false : t4;
  const Component = t5 === undefined ? "span" : t5;
  const startOnView = t6 === undefined ? true : t6;
  const showCursor = t7 === undefined ? true : t7;
  const blinkCursor = t8 === undefined ? true : t8;
  const cursorStyle = t9 === undefined ? "line" : t9;
  let t10;
  if ($[16] !== Component) {
    t10 = motion.create(Component, {
      forwardMotionProps: true
    });
    $[16] = Component;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const MotionComponent = t10;
  const [displayedText, setDisplayedText] = useState("");
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [phase, setPhase] = useState("typing");
  const elementRef = useRef(null);
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      amount: 0.3,
      once: true
    };
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const isInView = useInView(elementRef as React.RefObject<Element>, t11);
  let t12;
  if ($[19] !== children || $[20] !== words) {
    t12 = words || (children ? [children] : []);
    $[19] = children;
    $[20] = words;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const wordsToAnimate = t12;
  const hasMultipleWords = wordsToAnimate.length > 1;
  const typingSpeed = typeSpeed || duration;
  const deletingSpeed = deleteSpeed || typingSpeed / 2;
  const shouldStart = startOnView ? isInView : true;
  let t13;
  let t14;
  if ($[22] !== currentCharIndex || $[23] !== currentWordIndex || $[24] !== delay || $[25] !== deletingSpeed || $[26] !== displayedText || $[27] !== hasMultipleWords || $[28] !== loop || $[29] !== pauseDelay || $[30] !== phase || $[31] !== shouldStart || $[32] !== typingSpeed || $[33] !== wordsToAnimate) {
    t13 = () => {
      if (!shouldStart || wordsToAnimate.length === 0) {
        return;
      }
      const timeoutDelay = delay > 0 && displayedText === "" ? delay : phase === "typing" ? typingSpeed : phase === "deleting" ? deletingSpeed : pauseDelay;
      const timeout = setTimeout(() => {
        const currentWord = wordsToAnimate[currentWordIndex] || "";
        const graphemes = Array.from(currentWord);
        bb88: switch (phase) {
          case "typing":
            {
              if (currentCharIndex < graphemes.length) {
                setDisplayedText(graphemes.slice(0, currentCharIndex + 1).join(""));
                setCurrentCharIndex(currentCharIndex + 1);
              } else {
                if (hasMultipleWords || loop) {
                  const isLastWord = currentWordIndex === wordsToAnimate.length - 1;
                  if (!isLastWord || loop) {
                    setPhase("pause");
                  }
                }
              }
              break bb88;
            }
          case "pause":
            {
              setPhase("deleting");
              break bb88;
            }
          case "deleting":
            {
              if (currentCharIndex > 0) {
                setDisplayedText(graphemes.slice(0, currentCharIndex - 1).join(""));
                setCurrentCharIndex(currentCharIndex - 1);
              } else {
                const nextIndex = (currentWordIndex + 1) % wordsToAnimate.length;
                setCurrentWordIndex(nextIndex);
                setPhase("typing");
              }
            }
        }
      }, timeoutDelay);
      return () => clearTimeout(timeout);
    };
    t14 = [shouldStart, phase, currentCharIndex, currentWordIndex, displayedText, wordsToAnimate, hasMultipleWords, loop, typingSpeed, deletingSpeed, pauseDelay, delay];
    $[22] = currentCharIndex;
    $[23] = currentWordIndex;
    $[24] = delay;
    $[25] = deletingSpeed;
    $[26] = displayedText;
    $[27] = hasMultipleWords;
    $[28] = loop;
    $[29] = pauseDelay;
    $[30] = phase;
    $[31] = shouldStart;
    $[32] = typingSpeed;
    $[33] = wordsToAnimate;
    $[34] = t13;
    $[35] = t14;
  } else {
    t13 = $[34];
    t14 = $[35];
  }
  useEffect(t13, t14);
  let t15;
  if ($[36] !== currentWordIndex || $[37] !== wordsToAnimate) {
    t15 = Array.from(wordsToAnimate[currentWordIndex] || "");
    $[36] = currentWordIndex;
    $[37] = wordsToAnimate;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const currentWordGraphemes = t15;
  const isComplete = !loop && currentWordIndex === wordsToAnimate.length - 1 && currentCharIndex >= currentWordGraphemes.length && phase !== "deleting";
  const shouldShowCursor = showCursor && !isComplete && (hasMultipleWords || loop || currentCharIndex < currentWordGraphemes.length);
  let t16;
  if ($[39] !== cursorStyle) {
    t16 = () => {
      switch (cursorStyle) {
        case "block":
          {
            return "\u258C";
          }
        case "underscore":
          {
            return "_";
          }
        default:
          {
            return "|";
          }
      }
    };
    $[39] = cursorStyle;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const getCursorChar = t16;
  let t17;
  if ($[41] !== className) {
    t17 = cn("leading-[5rem] tracking-[-0.02em]", className);
    $[41] = className;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== blinkCursor || $[44] !== getCursorChar || $[45] !== shouldShowCursor) {
    t18 = shouldShowCursor && <span className={cn("inline-block", blinkCursor && "animate-blink-cursor")}>{getCursorChar()}</span>;
    $[43] = blinkCursor;
    $[44] = getCursorChar;
    $[45] = shouldShowCursor;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== MotionComponent || $[48] !== displayedText || $[49] !== props || $[50] !== t17 || $[51] !== t18) {
    t19 = <MotionComponent ref={elementRef} className={t17} {...props}>{displayedText}{t18}</MotionComponent>;
    $[47] = MotionComponent;
    $[48] = displayedText;
    $[49] = props;
    $[50] = t17;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  return t19;
}